import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import moment from 'moment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlusIcon from 'mdi-react/PlusIcon';
import BellOutlineIcon from 'mdi-react/BellOutlineIcon';
import { IsMobileContext } from '../../../core/context/is-mobile';
import * as styles from '../offices.styles';
import * as communicationStyles from '../office-communication.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';

import type OfficeNotification from '../models/office-notification';
import { Center } from '../../../core/components/center/center.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../core/styles.constants';
import OfficeNotificationCard from '../cards/office-notification-card.component';
import MessageGroup from '../cards/message-group.component';
import { notificationStatus } from '../message-status';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import notify from '../../../core/components/notify/notify.component';
import CreateNotificationModal from '../modals/create-office-notification-modal.component';
import { createOfficeNotification, deleteOfficeNotification, updateOfficeNotification } from '../services/office-notification.services';
import { maximumCharacters } from '../models/office.constants';
import AppConstants from '../../../core/app.constants';

interface Props {
  officeNotifications: OfficeNotification[];
  readonly officeId: string | undefined;
  officeNotificationsError: number | undefined;
}

const OfficeNotificationsGroup = ({
  officeNotifications: initialoOficeNotifications,
  officeId,
  officeNotificationsError: initialoOficeNotificationsError,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const isMobile = useContext(IsMobileContext);
  const [showCreateNotificationModal, setShowCreateNotificationModal] = useState(false);

  const [officeNotifications, setOfficeNotifications] = useState(initialoOficeNotifications);
  const sortedOfficeNotifications = officeNotifications?.sort(
    (a, b) => {
      const dateA = a.expirationDate ? new Date(a.expirationDate).getTime() : new Date(a.scheduledDateTime).getTime();
      const dateB = b.expirationDate ? new Date(b.expirationDate).getTime() : new Date(b.scheduledDateTime).getTime();
      return dateA - dateB;
    }
  ) ?? [];
  const handleShowCreateModal = (): void => {
    setShowCreateNotificationModal(true);
  };

  const keycloak = useKeycloak();

  const [isLoading, setIsLoading] = useState(true);

  const [officeNotificationsError] = useState<number | undefined>(initialoOficeNotificationsError);

  const handleClose = useCallback(() => {
    setShowCreateNotificationModal(false);
  }, []);

  const notificationIsValid = (officeNotificationToValidate: OfficeNotification): boolean => {
    if (officeNotificationToValidate.textDutch.length > maximumCharacters) {
      notify(t('office-notification-text-too-many-characters', { maximumCharacters }), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.textEnglish && !stringIsNullOrWhitespace(officeNotificationToValidate.textEnglish)
      && officeNotificationToValidate.textEnglish.length > maximumCharacters) {
      notify(t('office-notification-text-too-many-characters', { maximumCharacters }), false);
      setIsLoading(false);
      return false;
    }

    if (stringIsNullOrWhitespace(officeNotificationToValidate.textDutch)) {
      notify(t('office-notification-text-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.officeId === '') {
      notify(t('office-notification-invalid-office-id-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.scheduledDateTime == null) {
      notify(t('office-notification-scheduleddatetime-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.scheduledDateTime < new Date()) {
      notify(t('office-notification-past-scheduled-datetime-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.expirationDate == null && officeNotificationToValidate.isRecurring) {
      notify(t('office-notification-expirationdate-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.expirationDate != null &&
      officeNotificationToValidate.expirationDate < officeNotificationToValidate.scheduledDateTime) {
      notify(t('office-notification-invalid-date-range-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.expirationDate != null &&
      officeNotificationToValidate.expirationDate < moment().startOf('day').toDate()) {
      notify(t('office-notification-past-date-message'), false);
      setIsLoading(false);
      return false;
    }

    if (officeNotificationToValidate.isRecurring &&
        (!officeNotificationToValidate.recurringDays || officeNotificationToValidate.recurringDays.length === 0)) {
      notify(t('office-notification-recurring-days-empty-message'), false);
      setIsLoading(false);
      return false;
    }

    if (!officeNotificationToValidate.isRecurring && officeNotificationToValidate.recurringDays &&
      officeNotificationToValidate.recurringDays.length > 0) {
      notify(t('office-notification-recurring-days-invalid-message'), false);
      setIsLoading(false);
      return false;
    }

    return true;
  };

  const handleCreateNotification = useCallback(async (notification: OfficeNotification): Promise<void> => {
    if (stringIsNullOrWhitespace(notification.officeId)) return;

    const response = await createOfficeNotification(notification, keycloak);

    if (!response) {
      notify(t('office-notification-creation-error-message'), false);
      setIsLoading(false);
      return;
    }

    const officeNotificationsCopy = [...officeNotifications];
    officeNotificationsCopy.push(response);

    setOfficeNotifications(officeNotificationsCopy);

    notify(t('office-notification-creation-success-message'), true);

    handleClose();
  },
  [officeNotifications, keycloak, officeId],
  );

  const shouldUpdateNotification = async (originalNotification: OfficeNotification, updatedNotification: OfficeNotification):
    Promise<boolean> => {
    if (!officeId) return false;

    const response = await updateOfficeNotification(originalNotification, updatedNotification, keycloak);

    if (!response) {
      notify(t('office-notification-edit-error-message'), false);
      setIsLoading(false);
      return false;
    }

    const filteredofficeNotifications = officeNotifications?.filter((notification) => notification.id !== response.id);
    filteredofficeNotifications?.push(response);

    setOfficeNotifications(filteredofficeNotifications);

    notify(t('office-notification-edit-success-message'), true);

    return true;
  };

  const shouldDeleteNotification = async (notificationToDelete: OfficeNotification): Promise<boolean> => {
    const success = await deleteOfficeNotification(notificationToDelete, keycloak);
    if (success) {
      const remainingofficeNotifications = officeNotifications?.filter((notification) => notification.id !== notificationToDelete.id);
      setOfficeNotifications(remainingofficeNotifications);
      return true;
    }
    notify(t('delete-office-notification-error-message'), false);
    return false;
  };

  if (isLoading) {
    setIsLoading(false);
  }

  const getStatusMessage = useCallback(
    (statusCode: number) => {
      switch (statusCode) {
        case 401:
          return t('unauthorized-error');
        default:
          return t('unknown');
      }
    },
    [t]
  );

  const renderCard = (notification: OfficeNotification): React.JSX.Element => (
    <OfficeNotificationCard
      key={notification.id}
      initialNotification={notification}
      handleUpdateNotification={shouldUpdateNotification}
      notificationIsValid={notificationIsValid}
      onDelete={shouldDeleteNotification}
    />
  );

  // Grouped by what each message is doing now: still to go out, recurring inside its window, or
  // already delivered (folded away).
  const inStatus = (status: ReturnType<typeof notificationStatus>): OfficeNotification[] =>
    sortedOfficeNotifications.filter(
      (notification) => notificationStatus(notification.scheduledDateTime, notification.expirationDate) === status,
    );

  return (
    <div className={style(communicationStyles.card)}>
      <div className={style(communicationStyles.sectionHead(isMobile))}>
        <div className={style(communicationStyles.sectionHeadLeft)}>
          <div className={style(communicationStyles.tile)}>
            <BellOutlineIcon size={20} />
          </div>
          <div>
            <h2 className={style(communicationStyles.sectionTitle)}>
              {t('office-notifications')}
              <span className={style(communicationStyles.countPill)}>{sortedOfficeNotifications.length}</span>
              <InfoOutlinedIcon
                data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
                data-tooltip-content={t('office-notifications-info-message').toString()}
                sx={styles.tooltipIconSX}
              />
            </h2>
            <p className={style(communicationStyles.lede)}>{t('office-notifications-card-subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          className={`${style(surfaceStyles.primaryButton)} button_create_notification`}
          onClick={handleShowCreateModal}
          disabled={isLoading}
        >
          <PlusIcon size={16} />
          {t('create-office-notification')}
        </button>
      </div>
      <div className={style(communicationStyles.sectionBody)}>
        <div className={style(communicationStyles.bodyDivider)} />
        {((): React.JSX.Element => {
          if (isLoading) {
            return (
              <Center>
                <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
              </Center>
            );
          }

          if (officeNotificationsError) {
            return (
              <span className={style(communicationStyles.errorMessage)}>
                {t('office-notification-get-error')}
                {': '}
                {getStatusMessage(officeNotificationsError)}
              </span>
            );
          }

          if (sortedOfficeNotifications === undefined || sortedOfficeNotifications.length === 0) {
            return <span className={style(communicationStyles.emptyMessage)}>{t('no-office-notifications-available-message')}</span>;
          }

          const scheduled = inStatus('scheduled');
          const active = inStatus('active');
          const sent = inStatus('sent');

          return (
            <div>
              <MessageGroup label={t('message-status-scheduled') ?? ''} count={scheduled.length}>
                {scheduled.map(renderCard)}
              </MessageGroup>
              <MessageGroup label={t('message-status-live') ?? ''} count={active.length}>
                {active.map(renderCard)}
              </MessageGroup>
              <MessageGroup label={t('message-status-sent') ?? ''} count={sent.length} collapsible>
                {sent.map(renderCard)}
              </MessageGroup>
            </div>
          );
        })()}
      </div>
      <CreateNotificationModal
        showModal={showCreateNotificationModal}
        handleClose={handleClose}
        handleCreate={(notification): void => {
          handleCreateNotification(notification);
        }}
        isNotificationValid={notificationIsValid}
        officeId={officeId ?? ''}
      />
    </div>
  );
};

export default OfficeNotificationsGroup;
